import React, { useEffect, useState } from "react";
import NewsItem from "./NewsItem";
import Spinner from "./Spinner";
import PropTypes from "prop-types";
import InfiniteScroll from "react-infinite-scroll-component";

export default function News(props) {
  const [articles, setArticles] = useState([]);
  const [loading, setloading] = useState(true);
  const [page, setPage] = useState("");
  const [totalResults, setTotalResults] = useState(0);

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const cacheKey = `${props.category}_news_cache`;
  const cacheExpiry = 30 * 60 * 1000;
  let lastFetchTime = 0;

  const fetchNews = async (isInitial = false) => {
    const now = Date.now();
    const cached = JSON.parse(localStorage.getItem(cacheKey));

    if (isInitial && cached && now - cached.timestamp < cacheExpiry) {
      // Load from cache
      setArticles(cached.articles);
      setPage(cached.page);
      setTotalResults(cached.totalResults);
      setloading(false);
      return;
    }

    if (now - lastFetchTime < 10000) {
      console.log("Throttled: Please wait before fetching again");
      return;
    }

    lastFetchTime = now;

    props.setProgress(10);
    const baseUrl = `https://newsdata.io/api/1/news?apikey=${props.apiKey}&country=${props.country}&language=en,hi&category=${props.category}&size=${props.size}`;
    const url = page ? `${baseUrl}&page=${page}` : baseUrl;

    setloading(true);
    try {
      const response = await fetch(url);
      props.setProgress(30);
      const parsedData = await response.json();
      props.setProgress(70);
      const results = Array.isArray(parsedData.results)
        ? parsedData.results
        : [];
      setArticles(results);
      setPage(parsedData.nextPage || "");
      setTotalResults(parsedData.totalResults || 0);
      setloading(false);

      // Save to cache
      localStorage.setItem(
        cacheKey,
        JSON.stringify({
          articles: results,
          page: parsedData.nextPage || "",
          totalResults: parsedData.totalResults || 0,
          timestamp: now,
        })
      );

      props.setProgress(100);
    } catch (error) {
      console.error("Error fetching news:", error);
      setloading(false);
    }
  };

  useEffect(() => {
    document.title = `${capitalizeFirstLetter(props.category)} - InfoFusion`;
    fetchNews(true); // use cache if available
    // eslint-disable-next-line
  }, []);

  const fetchMoreData = async () => {
    if (loading || !page) return;

    const now = Date.now();
    if (now - lastFetchTime < 10000) {
      console.log("Throttled: Please wait before fetching more");
      return;
    }

    lastFetchTime = now;

    const baseUrl = `https://newsdata.io/api/1/news?apikey=${props.apiKey}&country=${props.country}&language=en,hi&category=${props.category}&size=${props.size}`;
    const url = `${baseUrl}&page=${page}`;

    setloading(true);
    try {
      const response = await fetch(url);
      const parsedData = await response.json();

      const results = Array.isArray(parsedData.results)
        ? parsedData.results
        : [];

      setArticles((prev) => prev.concat(results));

      setPage(parsedData.nextPage || "");
      setTotalResults(parsedData.totalResults || totalResults);
      setloading(false);
    } catch (error) {
      console.error("Error fetching more news:", error);
      setloading(false);
    }
  };
  return (
    <div className="container my-3">
      <h1 className="text-center" style={{ margin: "90px 0px 20px 0px" }}>
        {props.category === "top"
          ? "InfoFusion - Top Headlines"
          : `InfoFusion - Top ${capitalizeFirstLetter(
              props.category
            )} Headlines`}
      </h1>
      {loading && <Spinner />}
      <InfiniteScroll
        dataLength={articles.length}
        next={fetchMoreData}
        hasMore={articles.length !== totalResults}
        loader={<Spinner />}
      >
        <div className="container">
          <div className="row">
            {articles.map((element, index) => {
              return (
                <div
                  className="col-md-4"
                  key={`${element.article_id || index}-${index}`}
                >
                  <NewsItem
                    title={
                      element.title ? element.title.slice(0, 50) : "No Title"
                    }
                    description={
                      element.description
                        ? element.description.slice(0, 88)
                        : "No Description"
                    }
                    imageUrl={element.image_url || "/public/placeholder.webp"}
                    newsUrl={element.link || "#"}
                    author={element.creator || "Unknown"}
                    date={element.pubDate || "Today"}
                    source={element.source_name || "Unknown Source"}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </InfiniteScroll>
    </div>
  );
}

News.defaultProps = {
  country: "in",
  category: "top",
  size: 9,
};

News.propTypes = {
  country: PropTypes.string,
  category: PropTypes.string,
  size: PropTypes.number,
  apiKey: PropTypes.string.isRequired,
  setProgress: PropTypes.func.isRequired,
};
